import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'

// Types — V1 overlay config (legacy)
export interface OverlayConfig {
  enabled: boolean
  title: string
  subtitle: string
  thumbnail: string
  cta_text: string
  cta_url: string
  loading_duration: number
  bg_color: string
  accent_color: string
  close_delay: number
}

// V2 block types
export interface VideoBlock {
  type: 'video'
  enabled: boolean
  title: string
  subtitle: string
  thumbnail: string
  duration: number
  accent_color: string
}

export interface TypingBlock {
  type: 'typing'
  enabled: boolean
  messages: string[]
  speed: number
  delay_between: number
}

export interface ImageBlock {
  type: 'image'
  enabled: boolean
  url: string
  aspect_ratio: string
  border_radius: number
}

export interface CounterBlock {
  type: 'counter'
  enabled: boolean
  base_number: number
  variation: number
  text: string
}

export interface TimerBlock {
  type: 'timer'
  enabled: boolean
  minutes: number
  label: string
  on_zero: 'freeze' | 'redirect' | 'hide'
}

export interface Testimonial {
  name: string
  text: string
  stars: number
}

export interface SocialProofBlock {
  type: 'social_proof'
  enabled: boolean
  testimonials: Testimonial[]
  interval: number
}

export interface CtaBlock {
  type: 'cta'
  enabled: boolean
  text: string
  url: string
  color: string
  hint: string
  delay: number
}

export interface ArrowsBlock {
  type: 'arrows'
  enabled: boolean
  count: number
  color: string
}

export interface LoadingBlock {
  type: 'loading'
  enabled: boolean
  text: string
  color: string
}

export interface TextBlock {
  type: 'text'
  enabled: boolean
  content: string
  size: 'sm' | 'md' | 'lg' | 'xl'
  color: string
  bold: boolean
  uppercase: boolean
  align: 'left' | 'center' | 'right'
}

export interface AdSlotBlock {
  type: 'ad_slot'
  enabled: boolean
  template: 'video' | 'whatsapp' | 'security_gate'
  code: string
  code_2: string
  thumbnail: string
  title: string
  duration: number
  accent_color: string
  status_messages: string[]
  micro_commit_text: string
  micro_commit_delay: number
  play_text: string
  // WhatsApp-specific
  wpp_contact_name: string
  wpp_header_text: string
  wpp_avatar_url: string
  // Security Gate-specific
  gate_label: string
  gate_messages: string[]
  gate_message_interval: number
  gate_hint: string
  button_style: 'filled' | 'outline'
  external_verify: boolean
  verify_time: number | [number, number]
  verify_message: string
  verify_early_message: string
  fallback_url: string
  dd_messages: string[]
  dd_images: string[]
  gate_images: string[]
}

export type OverlayBlock = VideoBlock | TypingBlock | ImageBlock | CounterBlock | TimerBlock | SocialProofBlock | CtaBlock | ArrowsBlock | LoadingBlock | TextBlock | AdSlotBlock

export interface OverlayConfigV2 {
  enabled: boolean
  version: 2
  bg_color: string
  close_delay: number
  auto_dismiss: number
  ad_protect: boolean
  permanent: boolean
  debug: boolean
  max_views: number
  show_chance: number
  once_only: boolean
  cooldown_hours: number
  count_on: 'view' | 'click'
  bloco_topo: boolean
  bloco_topo_ad_unit?: string
  social_fallback?: boolean
  device_max_views?: number
  device_window_hours?: number
  datacenter_block?: boolean
  rate_limit_per_hour?: number
  dayparting?: {
    enabled?: boolean
    gam_window?: { start?: number; end?: number }
    gam?: { ad_unit?: string; passthrough?: number }
    adskeeper?: { loader?: string; widget?: string; passthrough?: number }
    cap?: { enabled?: boolean; gam_daily_usd?: number; gam_ecpm_usd?: number }
    ctr_breaker?: { enabled?: boolean; max_ctr_pct?: number; resume_ctr_pct?: number; window_minutes?: number }
  }
  blocks: OverlayBlock[]
}

export interface TrackerSite {
  id: string
  user_id: string
  name: string
  domain: string | null
  site_key: string
  is_active: boolean
  ux_config: OverlayConfig | OverlayConfigV2 | null
  created_at: string
}

export interface UtmEntry {
  source: string
  medium: string
  campaign: string
  views: number
}

export interface RealtimeData {
  online: number
  views_today: number
  unique_today: number
  hourly: { hour: string; views: number }[]
  pages: Record<string, number>
  referrers: Record<string, number>
  devices: { type: string; browser: string; views: number }[]
  locations: { country: string; region: string; city: string; views: number }[]
  campaigns: UtmEntry[]
}

export interface HistoryData {
  daily: { date: string; views: number; unique_visitors: number }[]
  pages: { page_path: string; views: number; unique_visitors: number }[]
  referrers: { referrer_domain: string; views: number }[]
  devices: { device_type: string; browser: string; views: number }[]
  locations: { country: string; region: string; city: string; views: number }[]
  campaigns: { utm_source: string; utm_medium: string; utm_campaign: string; views: number }[]
}

// Quality types
export interface QualityDistribution {
  hot: number
  warm: number
  cold: number
}

export interface QualityBySource {
  label: string
  sessions: number
  avg_score: number
  hot: number
  warm: number
  cold: number
  engagement_rate: number
}

export interface ActiveSession {
  session_id: string
  visitor_id: string
  duration: number
  pages: number
  scroll: number
  cta: boolean
  score: number
  quality: 'hot' | 'warm' | 'cold'
  entry_page: string
  source: string
}

export interface QualityRealtimeData {
  engagement_rate: number
  avg_score: number
  distribution: QualityDistribution
  by_source: QualityBySource[]
  active_sessions: ActiveSession[]
}

export interface QualityHistorySource {
  source_label: string
  total_sessions: number
  avg_score: number
  engagement_rate: number
  hot_pct: number
  warm_pct: number
  cold_pct: number
  avg_duration: number
  avg_pages: number
  avg_scroll: number
  cta_rate: number
}

export interface QualityHistoryDaily {
  date: string
  sessions: number
  avg_score: number
  engagement_rate: number
  hot: number
  warm: number
  cold: number
}

export interface QualityHistorySession {
  session_id: string
  visitor_id: string
  date: string
  utm_source: string | null
  utm_medium: string | null
  referrer_domain: string | null
  duration_seconds: number
  pages_count: number
  max_scroll: number
  cta_clicked: boolean
  score: number
  quality: 'hot' | 'warm' | 'cold'
  entry_page: string | null
}

export interface QualityHistoryData {
  summary: {
    total_sessions: number
    engagement_rate: number
    avg_score: number
    hot_pct: number
    warm_pct: number
    cold_pct: number
  }
  daily: QualityHistoryDaily[]
  by_source: QualityHistorySource[]
  sessions: {
    data: QualityHistorySession[]
    current_page: number
    last_page: number
    total: number
  }
}

// Analytics types (aba Analytics — clone whos.amung.us)
export interface AnalyticsTimelinePoint {
  hour: string
  online_min: number | null
  online_avg: number
  online_max: number | null
}

export interface AnalyticsReader {
  path: string
  title: string
  count: number
  pct: number
}

export interface AnalyticsDevice {
  type: 'android' | 'apple' | 'windows' | 'other'
  count: number
}

export interface AnalyticsDeviceHistory {
  hour: string
  android: number
  apple: number
  windows: number
}

export interface AnalyticsHourlyViews {
  hour: string
  views: number
  sessions: number
}

export interface AnalyticsCountry {
  cc: string
  name: string
  count: number
}

export interface AnalyticsData {
  online: number
  timeline: AnalyticsTimelinePoint[]
  readers: AnalyticsReader[]
  devices: AnalyticsDevice[]
  device_history: AnalyticsDeviceHistory[]
  hourly_views: AnalyticsHourlyViews[]
  countries: AnalyticsCountry[]
}

// Hooks
export function useTrackerSites() {
  return useQuery<TrackerSite[]>({
    queryKey: ['tracker-sites'],
    queryFn: () => api.get<TrackerSite[]>('/tracker/sites'),
  })
}

export function useCreateTrackerSite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; domain?: string }) =>
      api.post<TrackerSite>('/tracker/sites', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tracker-sites'] }),
  })
}

export function useUpdateTrackerSite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; domain?: string; is_active?: boolean; ux_config?: OverlayConfig | OverlayConfigV2 }) =>
      api.put<TrackerSite>(`/tracker/sites/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tracker-sites'] }),
  })
}

export function useDeleteTrackerSite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/tracker/sites/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tracker-sites'] }),
  })
}

export function useTrackerRealtime(siteId: string | undefined) {
  return useQuery<RealtimeData>({
    queryKey: ['tracker-realtime', siteId],
    queryFn: () => api.get<RealtimeData>(`/tracker/sites/${siteId}/realtime`),
    enabled: !!siteId,
    refetchInterval: 5000,
  })
}

export interface ProtectionData {
  days: number
  served: number
  blocked: number
  by_layer: Record<string, number>
}

export function useTrackerProtection(siteId: string | undefined, days = 1) {
  return useQuery<ProtectionData>({
    queryKey: ['tracker-protection', siteId, days],
    queryFn: () => api.get<ProtectionData>(`/tracker/sites/${siteId}/protection?days=${days}`),
    enabled: !!siteId,
    refetchInterval: 15000,
  })
}

export function useTrackerHistory(siteId: string | undefined, days = 30) {
  return useQuery<HistoryData>({
    queryKey: ['tracker-history', siteId, days],
    queryFn: () => api.get<HistoryData>(`/tracker/sites/${siteId}/history?days=${days}`),
    enabled: !!siteId,
  })
}

// Histórico por preset de período (yesterday | 7d | 30d). Reusa o endpoint /history.
export function useTrackerHistoryPeriod(siteId: string | undefined, period: string, enabled = true) {
  return useQuery<HistoryData>({
    queryKey: ['tracker-history-period', siteId, period],
    queryFn: () => api.get<HistoryData>(`/tracker/sites/${siteId}/history?period=${period}`),
    enabled: !!siteId && enabled,
  })
}

export interface NetworkStatusData {
  enabled: boolean
  hour: number
  network?: string | null
  reason?: string | null
  reason_label?: string
  gam_killed?: boolean
  gam_window?: { start?: number; end?: number } | null
}

export function useTrackerNetworkStatus(siteId: string | undefined) {
  return useQuery<NetworkStatusData>({
    queryKey: ['tracker-network-status', siteId],
    queryFn: () => api.get<NetworkStatusData>(`/tracker/sites/${siteId}/network-status`),
    enabled: !!siteId,
    refetchInterval: 30000,
  })
}

export function useTrackerSnippet(siteId: string | undefined) {
  return useQuery<{ snippet: string; script_url: string }>({
    queryKey: ['tracker-snippet', siteId],
    queryFn: () => api.get<{ snippet: string; script_url: string }>(`/tracker/sites/${siteId}/snippet`),
    enabled: !!siteId,
  })
}

export function useTrackerAnalytics(siteId: string | undefined) {
  return useQuery<AnalyticsData>({
    queryKey: ['tracker-analytics', siteId],
    queryFn: () => api.get<AnalyticsData>(`/tracker/sites/${siteId}/analytics`),
    enabled: !!siteId,
    refetchInterval: 5_000,
  })
}
